import clsx from 'clsx'
import { Link } from '@tanstack/react-router'
import { useState, useMemo, ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Can, useAccessContext } from '@/auth/access'
import { canAccessUsersSection, canAccessCompaniesSection, canAccessStaffSection, canAccessStoresSection, isSystemContext } from '@tandem/schema/permissions'
import $ from './styles.module.css'
import Logo from '@tandem/ui/Тандем.svg?react'
import Dropdown from '@tandem/ui/Dropdown'
import md5 from 'md5'
import { SidecarContext } from './sidecar'
import { Building2Icon, PackageIcon, StoreIcon, Users2Icon, UsersRoundIcon } from 'lucide-react'

export default function Layout({ children }) {
  let { user, logout, company, companies, setActiveCompanyId } = useAuth()
  let userEmail = user?.email || ''
  let gravatarUrl = getGravatarUrl(userEmail)
  let inSystemContext = isSystemContext(useAccessContext())

  let [menuOpen, setMenuOpen] = useState(false)
  let [sidecar, setSidecar] = useState<ReactNode>(null)
  let sidecarContextValue = useMemo(() => ({ content: sidecar, setContent: setSidecar }), [sidecar])

  return (
    <SidecarContext.Provider value={sidecarContextValue}>
      <div class={$.layout}>
        
        <header class={$.header}>
          <div class={$.logoWrapper}>{/* Side elements of header have flex-grow:1 making SVG centerified w/o wrapper. */}
            <Link to='/'>
              <Logo class={$.logo} />
            </Link>
          </div>
          
          <ul class={$.nav}>
            <Can check={canAccessUsersSection}>
              <li>
                <Link to='/users' activeProps={{ className: $.active }} className={clsx($.link, $.system)}>
                  <Users2Icon />
                  <span>Пользователи</span>
                </Link>
              </li>
            </Can>
            <Can check={canAccessCompaniesSection}>
              <li>
                <Link to='/companies' activeProps={{ className: $.active }} className={clsx($.link, $.system)}>
                  <Building2Icon />
                  <span>Компании</span>
                </Link>
              </li>
            </Can>
            <li>
              <Link to='/catalog' activeProps={{ className: $.active }} className={$.link}>
                <PackageIcon />
                <span>Каталог</span>
              </Link>
            </li>
            <Can check={canAccessStoresSection}>
              <li>
                <Link to='/stores' activeProps={{ className: $.active }} className={$.link}>
                  <StoreIcon />
                  <span>Торговые точки</span>
                </Link>
              </li>
            </Can>
            <Can check={canAccessStaffSection}>
              <li>
                <Link to='/staff' activeProps={{ className: $.active }} className={$.link}>
                  <UsersRoundIcon />
                  <span>Сотрудники</span>
                </Link>
              </li>
            </Can>
          </ul>

          <Dropdown
            open={menuOpen}
            onOpenChange={setMenuOpen}
            align='left'
            verticalAlign='bottom'
            trigger={
              <button class={$.userMenuTrigger} type='button'>
                <img src={gravatarUrl} class={clsx($.userpic, inSystemContext && $.system)} />
              </button>
            }
            class={$.authDropdown}
          >
            <div class={$.userMenu}>
              {companies.length > 0 && (
                <>
                  {companies.map((c) => (
                    <button
                      key={c.company_id}
                      type='button'
                      class={clsx(
                        $.menuOption,
                        c.company_id === company?.company_id && $.menuOptionActive,
                      )}
                      onClick={() => {
                        setActiveCompanyId(c.company_id)
                        setMenuOpen(false)
                      }}
                    >
                      <span>{c.name}</span>
                    </button>
                  ))}
                  <hr class={$.menuDivider} />
                </>
              )}
              <Link to='/settings' className={$.menuOption} activeProps={{ className: $.menuOptionActive }} onClick={() => setMenuOpen(false)}>
                Настройки
              </Link>
              <button
                class={$.menuOption}
                type='button'
                onClick={() => {
                  logout()
                  setMenuOpen(false)
                }}
              >
                Выйти
              </button>
            </div>
          </Dropdown>
        </header>

        <div class={$.body}>
          <main class={$.content}>{children}</main>
          {sidecar && <aside class={$.sidecar}>{sidecar}</aside>}
        </div>

        <footer class={$.footer}>© 2026 ООО «Тандем Ритейл»</footer>
      </div>
    </SidecarContext.Provider>
  )
}

function getGravatarUrl(email: string, size = 80) {
  let hash = md5(email.toLowerCase().trim())
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`
}
